import React, { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { employeeAPI, organizationAPI, verticalAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  Users,
  Loader2,
  Filter,
  Download,
  Search,
  Building2,
  Layers,
} from 'lucide-react';

export default function EmployeeSalaryReportPage() {
  const { canViewSalary } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterOrg, setFilterOrg] = useState('all');
  const [filterVertical, setFilterVertical] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      loadEmployees();
    }
  }, [filterOrg, filterVertical]);

  const loadData = async () => {
    try {
      const [orgRes, vertRes] = await Promise.all([
        organizationAPI.getAll(),
        verticalAPI.getAll(),
      ]);
      setOrganizations(orgRes.data);
      setVerticals(vertRes.data);
      await loadEmployees();
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const params = {};
      if (filterOrg !== 'all') params.organization_id = filterOrg;
      if (filterVertical !== 'all') params.vertical_id = filterVertical;

      const response = await employeeAPI.getAll(params);
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  // Get organization name by ID
  const getOrgName = (id) => organizations.find(o => o.id === id)?.name || '-';
  
  // Get vertical name by ID  
  const getVerticalName = (id) => verticals.find(v => v.id === id)?.name || '-';

  // Filtered employees based on search
  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) return employees;
    
    const query = searchQuery.toLowerCase();
    return employees.filter(emp => 
      emp.name?.toLowerCase().includes(query) ||
      emp.employee_id?.toLowerCase().includes(query)
    );
  }, [employees, searchQuery]);

  // Calculate totals
  const totals = useMemo(() => {
    return filteredEmployees.reduce((acc, emp) => {
      const gross = emp.gross_salary || emp.monthly_salary || 0;
      const retention = emp.retention_amount || emp.retention_bonus || 0;
      const netEligible = gross - retention;
      const annualBonus = emp.annual_bonus || 0;
      
      return {
        grossSalary: acc.grossSalary + gross,
        retention: acc.retention + retention,
        netEligible: acc.netEligible + netEligible,
        annualBonus: acc.annualBonus + annualBonus,
        count: acc.count + 1,
      };
    }, { grossSalary: 0, retention: 0, netEligible: 0, annualBonus: 0, count: 0 });
  }, [filteredEmployees]);

  const handleExportCSV = () => {
    // Generate CSV
    const headers = ['Employee ID', 'Name', 'Organization', 'Vertical', 'Gross Salary', 'Retention', 'Net Eligible', 'Annual Bonus'];
    const rows = filteredEmployees.map(emp => {
      const gross = emp.gross_salary || emp.monthly_salary || 0;
      const retention = emp.retention_amount || emp.retention_bonus || 0;
      return [
        emp.employee_id,
        emp.name,
        getOrgName(emp.organization_id),
        getVerticalName(emp.vertical_id),
        gross,
        retention,
        gross - retention,
        emp.annual_bonus || 0,
      ];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_salary_report.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  const handleExportPDF = () => {
    const dateStr = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Build table rows HTML
    let tableRowsHTML = filteredEmployees.map(emp => {
      const gross = emp.gross_salary || emp.monthly_salary || 0;
      const retention = emp.retention_amount || emp.retention_bonus || 0;
      return `
        <tr>
          <td>${emp.employee_id}</td>
          <td>${emp.name}</td>
          <td>${getOrgName(emp.organization_id)}</td>
          <td>${getVerticalName(emp.vertical_id)}</td>
          <td class="right">${formatCurrency(gross)}</td>
          <td class="right">${formatCurrency(retention)}</td>
          <td class="right">${formatCurrency(gross - retention)}</td>
          <td class="right">${formatCurrency(emp.annual_bonus || 0)}</td>
        </tr>
      `;
    }).join('');

    const filterInfo = [];
    if (filterOrg !== 'all') filterInfo.push(`Organization: ${getOrgName(filterOrg)}`);
    if (filterVertical !== 'all') filterInfo.push(`Vertical: ${getVerticalName(filterVertical)}`);

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Employee Salary Report - PayTrack</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 40px; color: #333; }
          .header { border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; margin-bottom: 20px; }
          .header h1 { font-size: 24px; color: #111; }
          .header p { color: #666; font-size: 14px; margin-top: 4px; }
          .filters { background: #f9fafb; padding: 12px; border-radius: 6px; margin-bottom: 20px; font-size: 13px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { padding: 10px 8px; text-align: left; border-bottom: 1px solid #e5e7eb; }
          th { background: #f3f4f6; font-weight: 600; color: #374151; }
          .right { text-align: right; }
          .totals { background: #f0f9ff; font-weight: 600; }
          .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #999; text-align: center; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Employee Salary Report</h1>
          <p>Generated on ${dateStr} • ${filteredEmployees.length} employees</p>
        </div>
        ${filterInfo.length > 0 ? `<div class="filters">Filters: ${filterInfo.join(' | ')}</div>` : ''}
        <table>
          <thead>
            <tr>
              <th>Employee ID</th>
              <th>Name</th>
              <th>Organization</th>
              <th>Vertical</th>
              <th class="right">Gross Salary</th>
              <th class="right">Retention</th>
              <th class="right">Net Eligible</th>
              <th class="right">Annual Bonus</th>
            </tr>
          </thead>
          <tbody>
            ${tableRowsHTML}
          </tbody>
          <tfoot>
            <tr class="totals">
              <td colspan="4"><strong>Total (${totals.count} employees)</strong></td>
              <td class="right">${formatCurrency(totals.grossSalary)}</td>
              <td class="right">${formatCurrency(totals.retention)}</td>
              <td class="right">${formatCurrency(totals.netEligible)}</td>
              <td class="right">${formatCurrency(totals.annualBonus)}</td>
            </tr>
          </tfoot>
        </table>
        <div class="footer">PayTrack - Salary Reporting System</div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
    toast.success('PDF export ready for printing');
  };

  if (!canViewSalary()) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">You do not have permission to view salary data.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="employee-salary-report-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Employee Salary Report</h1>
            <p className="text-muted-foreground mt-1">
              View salary components for all employees
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-2" />
              CSV
            </Button>
            <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="space-y-2">
                <Label>Search Employee</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="search-employee-input"
                  />
                </div>
              </div>

              {/* Organization Filter */}
              <div className="space-y-2">
                <Label>Organization</Label>
                <Select value={filterOrg} onValueChange={setFilterOrg}>
                  <SelectTrigger data-testid="filter-org-select">
                    <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="All Organizations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organizations</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Vertical Filter */}
              <div className="space-y-2">
                <Label>Vertical</Label>
                <Select value={filterVertical} onValueChange={setFilterVertical}>
                  <SelectTrigger data-testid="filter-vertical-select">
                    <Layers className="h-4 w-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="All Verticals" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Verticals</SelectItem>
                    {verticals.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Employees ({filteredEmployees.length})
            </CardTitle>
            <CardDescription>Salary breakdown for each employee</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No employees found</p>
              </div>
            ) : (
              <div className="overflow-auto" style={{ maxHeight: '500px' }}>
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="min-w-[100px]">Employee ID</TableHead>
                      <TableHead className="min-w-[150px]">Name</TableHead>
                      <TableHead className="min-w-[150px]">Organization</TableHead>
                      <TableHead className="min-w-[120px]">Vertical</TableHead>
                      <TableHead className="text-right min-w-[120px]">Gross Salary</TableHead>
                      <TableHead className="text-right min-w-[100px]">Retention</TableHead>
                      <TableHead className="text-right min-w-[120px]">Net Eligible</TableHead>
                      <TableHead className="text-right min-w-[100px]">Annual Bonus</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEmployees.map((emp) => {
                      const gross = emp.gross_salary || emp.monthly_salary || 0;
                      const retention = emp.retention_amount || emp.retention_bonus || 0;
                      const netEligible = gross - retention;
                      const annualBonus = emp.annual_bonus || 0;

                      return (
                        <TableRow key={emp.id} data-testid={`emp-row-${emp.id}`}>
                          <TableCell className="font-mono text-sm">{emp.employee_id}</TableCell>
                          <TableCell className="font-medium">{emp.name}</TableCell>
                          <TableCell>{getOrgName(emp.organization_id)}</TableCell>
                          <TableCell>{getVerticalName(emp.vertical_id)}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(gross)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-amber-600 dark:text-amber-400">
                            {retention > 0 ? formatCurrency(retention) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(netEligible)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-violet-600 dark:text-violet-400">
                            {annualBonus > 0 ? formatCurrency(annualBonus) : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    
                    {/* Totals Row */}
                    <TableRow className="bg-muted/50 font-semibold border-t-2">
                      <TableCell colSpan={4} className="text-right">
                        Total ({totals.count} employees)
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(totals.grossSalary)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-amber-600 dark:text-amber-400">
                        {formatCurrency(totals.retention)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(totals.netEligible)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-violet-600 dark:text-violet-400">
                        {formatCurrency(totals.annualBonus)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card className="bg-muted/30">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Total Gross Salary</p>
              <p className="text-2xl font-bold font-mono">{formatCurrency(totals.grossSalary)}</p>
            </CardContent>
          </Card>
          <Card className="bg-muted/30">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Total Retention</p>
              <p className="text-2xl font-bold font-mono text-amber-600 dark:text-amber-400">
                {formatCurrency(totals.retention)}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-muted/30">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Total Net Eligible</p>
              <p className="text-2xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
                {formatCurrency(totals.netEligible)}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-muted/30">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Total Annual Bonus</p>
              <p className="text-2xl font-bold font-mono text-violet-600 dark:text-violet-400">
                {formatCurrency(totals.annualBonus)}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
